import type { ReactNode } from 'react'

import { Badge, Field, Heading, Input, Label, Switch, Text } from '@ankor-io/ui-components'

export type OrganizationFeeCardProps = {
  title: string
  description: string
  /** Current (override) value as a string to allow empty input while editing. */
  value: string
  onValueChange: (value: string) => void
  /** Platform default shown (and used) when the card is on global defaults. */
  globalDefaultValue: number
  useGlobalDefaults: boolean
  onToggleGlobalDefaults: () => void
  /** Extra section rendered under the input (e.g. connected-account overrides for deposits). */
  children?: ReactNode
}

/**
 * A single pricing configuration card: label, value input and its own "use global defaults"
 * toggle. Each fee source gets its own card so they can be overridden independently. Saving is
 * handled by the parent section's single save action.
 */
export function OrganizationFeeCard({
  title,
  description,
  value,
  onValueChange,
  globalDefaultValue,
  useGlobalDefaults,
  onToggleGlobalDefaults,
  children,
}: OrganizationFeeCardProps) {
  return (
    <div className="flex flex-col rounded-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex flex-col gap-1">
          <Heading level={5}>{title}</Heading>
          <Text className="text-sm text-gray-600 dark:text-gray-400">{description}</Text>
        </div>
        <div className="flex items-center gap-3">
          <Badge color={useGlobalDefaults ? 'green' : 'gray'}>Use Global Defaults</Badge>
          <Switch
            checked={useGlobalDefaults}
            onChange={onToggleGlobalDefaults}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 px-4 pb-4">
        <Field>
          <Label className="text-sm/6 font-medium text-gray-700 dark:text-gray-300">{title} (%)</Label>
          <Input
            type="text"
            inputMode="decimal"
            disabled={useGlobalDefaults}
            readOnly={useGlobalDefaults}
            value={useGlobalDefaults ? String(globalDefaultValue) : value}
            onChange={(event) => onValueChange(event.target.value)}
          />
        </Field>

        {children}
      </div>
    </div>
  )
}
